/**
 * ============================================================================
 * NOTIFICATION SERVICE - Frontend
 * ============================================================================
 * 
 * Frontend service for managing notifications:
 * - Get notifications
 * - Mark as read/unread
 * - Delete notifications
 * - Notification preferences
 * - Push notification token management
 */

import api from './api';

/**
 * Handle API errors
 */
const handleApiError = (error) => {
  if (error.response) {
    return error.response.data;
  }
  return { message: error.message || 'An error occurred' };
};

/**
 * Get user notifications
 * @param {Object} filters - Filter options
 * @param {string} filters.type - Notification type filter
 * @param {boolean} filters.read - Read/unread filter
 * @param {number} filters.page - Page number
 * @param {number} filters.limit - Items per page
 * @returns {Promise<Object>} Notifications with pagination
 */
export const getNotifications = async (filters = {}) => {
  try {
    const response = await api.get('/notifications', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get unread notification count
 * @returns {Promise<number>} Unread count
 */
export const getUnreadCount = async () => {
  try {
    const response = await api.get('/notifications/unread-count');
    const data = response.data || {};
    return data.count ?? data.data?.count ?? 0;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get notification by ID
 * @param {string} notificationId - Notification ID
 * @returns {Promise<Object>} Notification details
 */
export const getNotification = async (notificationId) => {
  try {
    const response = await api.get(`/notifications/${notificationId}`);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Mark notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const markAsRead = async (notificationId) => {
  try {
    await api.patch(`/notifications/${notificationId}/read`);
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Mark notification as unread
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const markAsUnread = async (notificationId) => {
  try {
    await api.patch(`/notifications/${notificationId}/unread`);
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Mark all notifications as read
 * @returns {Promise<void>}
 */
export const markAllAsRead = async () => {
  try {
    await api.patch('/notifications/read-all');
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Delete notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export const deleteNotification = async (notificationId) => {
  try {
    await api.delete(`/notifications/${notificationId}`);
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Delete all notifications
 * @param {Object} filters - Optional filters
 * @returns {Promise<void>}
 */
export const deleteAllNotifications = async (filters = {}) => {
  try {
    await api.delete('/notifications', {
      params: filters,
    });
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get notification preferences
 * @returns {Promise<Object>} Notification preferences
 */
export const getPreferences = async () => {
  try {
    const response = await api.get('/notifications/preferences');
    return response.data.preferences;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Update notification preferences
 * @param {Object} preferences - Preference updates
 * @returns {Promise<Object>} Updated preferences
 */
export const updatePreferences = async (preferences) => {
  try {
    const response = await api.patch('/notifications/preferences', preferences);
    return response.data.preferences;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Register push notification token
 * @param {string} token - FCM push token
 * @param {string} platform - Platform (web, ios, android)
 * @returns {Promise<void>}
 */
export const registerPushToken = async (token, platform = 'web') => {
  try {
    await api.post('/notifications/push/register', {
      token,
      platform,
    });
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Unregister push notification token
 * @param {string} token - FCM push token
 * @returns {Promise<void>}
 */
export const unregisterPushToken = async (token) => {
  try {
    await api.post('/notifications/push/unregister', {
      token,
    });
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Subscribe to notification topic
 * @param {string} topic - Topic name
 * @returns {Promise<void>}
 */
export const subscribeToTopic = async (topic) => {
  try {
    await api.post('/notifications/push/subscribe', {
      topic,
    });
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Unsubscribe from notification topic
 * @param {string} topic - Topic name
 * @returns {Promise<void>}
 */
export const unsubscribeFromTopic = async (topic) => {
  try {
    await api.post('/notifications/push/unsubscribe', {
      topic,
    });
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get notification statistics
 * @returns {Promise<Object>} Notification statistics
 */
export const getStatistics = async () => {
  try {
    const response = await api.get('/notifications/statistics');
    return response.data.statistics;
  } catch (error) {
    throw handleApiError(error);
  }
};

export default {
  getNotifications,
  getUnreadCount,
  getNotification,
  markAsRead,
  markAsUnread,
  markAllAsRead,
  deleteNotification,
  deleteAllNotifications,
  getPreferences,
  updatePreferences,
  registerPushToken,
  unregisterPushToken,
  subscribeToTopic,
  unsubscribeFromTopic,
  getStatistics,
};
